import { IBAN_LENGTHS } from '@constants/lengthIban';
import { VALIDATION_REGEX } from '@constants/patterns';

export const removeAccents = (str: string): string =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const validateSpaces = (
  value: string | undefined,
  t: (key: string) => string,
  options: { checkConsecutive?: boolean } = { checkConsecutive: true }
): boolean | string => {
  if (!value) return true;

  if (/^\s|\s$/.test(value)) {
    return value.startsWith(' ')
      ? t('regNameStartSpace')
      : t('regNameEndSpace');
  }

  if (options.checkConsecutive !== false && /\s\s/.test(value))
    return t('regNameConsecutiveSpaces');
  if (value.trim() === '') return t('regNameOnlySpaces');

  return true;
};

export const formatUpperNoAccents = (value: string): string =>
  removeAccents(value.toUpperCase());

export const normalizeAddressForValidation = (address: string): string =>
  address
    .normalize('NFD') // Separate diacritics (Unicode normalization)
    .replace(/\p{M}+/gu, '') // Remove diacritics using Unicode property
    .replace(/[\u2010-\u2015]/g, '-') // Normalize all dash types to hyphen
    .replace(/[._/\\#''"()·•,:;|]+/g, ' ') // Common punctuation → space
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim()
    .toUpperCase();

export const validatePoBox = (
  address: string | undefined,
  t: (key: string) => string
): boolean | string => {
  if (!address) return true;

  const normalizedAddress = normalizeAddressForValidation(address);

  if (VALIDATION_REGEX.PO_BOX.test(normalizedAddress)) {
    return t('ccPoBoxError');
  }

  return true;
};

export const validateIban = (iban: string | null): boolean => {
  if (!iban) return false;
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  if (cleanIban.length < 4) {
    return false;
  }

  const countryCode = cleanIban.substring(0, 2);
  if (!(countryCode in IBAN_LENGTHS)) {
    return false;
  }

  const expectedLength = IBAN_LENGTHS[countryCode];
  if (cleanIban.length !== expectedLength) {
    return false;
  }

  const rearranged = cleanIban.substring(4) + cleanIban.substring(0, 4);

  const ibanToNumeric = (str: string): string =>
    str
      .split('')
      .map((char) => {
        if (/[A-Z]/.test(char)) {
          return (char.charCodeAt(0) - 55).toString();
        }
        return char;
      })
      .join('');

  const numericIban = ibanToNumeric(rearranged);

  try {
    const remainder = BigInt(numericIban) % BigInt(97);
    return remainder === BigInt(1);
  } catch (e) {
    return false;
  }
};
